import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { getInvitationForSelf, respondCompanyInvitation } from '../../services/api';
import { colors } from '../../designTokens';

const STORAGE_KEY = 'pendingCompanyInviteId';

export default function PendingCompanyInviteGate() {
  const { isAuthenticated, isLoading, user, refreshUserProfile } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const clearPending = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  const loadPending = useCallback(async () => {
    let id;
    try {
      id = sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return;
    }
    if (!id || !user?.email) return;
    try {
      const data = await getInvitationForSelf(id);
      const inv = data.invitation;
      if (!inv || inv.status !== 'PENDING') {
        clearPending();
        return;
      }
      if (inv.expiresAt && new Date(inv.expiresAt) < new Date()) {
        clearPending();
        return;
      }
      if (inv.companyId && inv.companyId === user.companyId) {
        clearPending();
        return;
      }
      setInvitation(inv);
      setOpen(true);
    } catch {
      clearPending();
    }
  }, [user?.email, user?.companyId]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return;
    loadPending();
  }, [isLoading, isAuthenticated, user, loadPending, location.pathname, location.search]);

  const respondToInvitation = async (action) => {
    if (!invitation?.id) return;
    setBusy(true);
    setError('');
    try {
      await respondCompanyInvitation(invitation.id, action);
      clearPending();
      setOpen(false);
      setInvitation(null);
      await refreshUserProfile();
    } catch (e) {
      setError(e.message || 'Failed to accept invitation');
    } finally {
      setBusy(false);
    }
  };

  const handleAccept = () => respondToInvitation('accept');
  const handleAcceptAndAbandon = () => respondToInvitation('accept_and_abandon');

  const handleReject = async () => {
    if (!invitation?.id) return;
    setBusy(true);
    setError('');
    try {
      await respondCompanyInvitation(invitation.id, 'reject');
      clearPending();
      setOpen(false);
      setInvitation(null);
    } catch (e) {
      setError(e.message || 'Failed to decline invitation');
    } finally {
      setBusy(false);
    }
  };

  if (!open || !invitation) return null;

  const requiresAbandon = invitation.canAbandonCurrentCompany;
  const destinationName = invitation.companyName || 'the new organization';
  const currentCompanyName = invitation.currentCompanyName || 'your current company';

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 24px 80px rgba(15, 23, 42, 0.22)',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 2.5,
          pb: 1,
          fontSize: '1.125rem',
          fontWeight: 650,
          letterSpacing: '-0.01em',
          color: colors.textPrimary,
        }}
      >
        Organization invitation
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 0 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body1" sx={{ mb: 1, color: colors.textPrimary, lineHeight: 1.6 }}>
          You&apos;ve been invited to join <strong>{destinationName}</strong>
          {invitation.teamName ? (
            <>
              {' '}
              (team: <strong>{invitation.teamName}</strong>)
            </>
          ) : null}
          .
        </Typography>
        {invitation.currentCompanyName && (
          <Typography variant="body2" sx={{ color: colors.textSecondary, lineHeight: 1.6 }}>
            You currently belong to <strong>{currentCompanyName}</strong>.
          </Typography>
        )}
        {requiresAbandon && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              border: '1px solid rgba(29, 78, 216, 0.16)',
              bgcolor: 'rgba(29, 78, 216, 0.06)',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ mb: 0.75, fontSize: '0.9375rem', fontWeight: 650, color: colors.textPrimary }}
            >
              To join, abandon your current company first
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, lineHeight: 1.6 }}>
              You are the only user in <strong>{currentCompanyName}</strong>, so PromptCrew can&apos;t leave that company
              ownerless. If you continue, we&apos;ll move your capabilities, knowledge, and projects into{' '}
              <strong>{destinationName}</strong> and remove the old company.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1.25, color: colors.textSecondary, lineHeight: 1.6 }}>
              To keep <strong>{currentCompanyName}</strong>, add another user to that account first, then come back to
              this invitation.
            </Typography>
          </Box>
        )}
        {!requiresAbandon && invitation.abandonUnavailableReason && (
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            {invitation.abandonUnavailableReason}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2.5, gap: 1.25, alignItems: 'center' }}>
        <Button
          onClick={handleReject}
          disabled={busy}
          variant="outlined"
          sx={{
            minHeight: 44,
            px: 2,
            borderColor: '#D8DEE8',
            color: colors.textSecondary,
            textTransform: 'none',
            fontWeight: 650,
            '&:hover': {
              borderColor: '#CBD5E1',
              bgcolor: 'rgba(15, 23, 42, 0.03)',
            },
          }}
        >
          Not now
        </Button>
        <Box sx={{ flex: 1 }} />
        {requiresAbandon ? (
          <Button
            onClick={handleAcceptAndAbandon}
            disabled={busy}
            variant="contained"
            sx={{ minHeight: 44, px: 2.5, textTransform: 'none', fontWeight: 700 }}
          >
            {busy ? <CircularProgress size={22} color="inherit" /> : `Abandon and join ${destinationName}`}
          </Button>
        ) : (
          <Button
            onClick={handleAccept}
            disabled={busy}
            variant="contained"
            sx={{ minHeight: 44, px: 2.5, textTransform: 'none', fontWeight: 700 }}
          >
            {busy ? <CircularProgress size={22} color="inherit" /> : 'Join organization'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
